// Libs
import { useCallback, useState } from "react";
import { useTranslation } from "src/design-system/core/TranslationContext";
import useAuth from "src/hooks/useAuth";
import type { IdStringName } from "src/types/IdName";
import { MarketingActionType } from "src/types/enum/MarketingActionType";
import { displaySmartScheduler } from "src/utils/ABTesting/displaySmartScheduler";

// Types
import type { MarketingActionActivationTypeEnum } from "src/utils/activationTypes";
import { MarketingActionVariantEnum } from "src/utils/variants";

const ACTION_TYPE_DEFAULT_VALUE = [];

export type UseActionTypeFilterValue = MarketingActionActivationTypeEnum[];

export interface UseActionTypeFilterOptions {
    defaultValue?: UseActionTypeFilterValue;
    initialValue?: UseActionTypeFilterValue;
    marketingActionType?: MarketingActionType;
}

export interface UseActionTypeFilterResult {
    actionTypeOptions: IdStringName[];
    actionTypes: UseActionTypeFilterValue;
    handleActionTypeChange: (actionTypes: UseActionTypeFilterValue) => void;
    resetFilter: () => void;
    isEmpty: boolean;
}

const ACTION_TYPES_OFFER: MarketingActionVariantEnum[] = [
    MarketingActionVariantEnum.Classic,
    MarketingActionVariantEnum.AutoRule,
    MarketingActionVariantEnum.SmartScheduler,
    MarketingActionVariantEnum.Special,
    MarketingActionVariantEnum.CoFunded,
];

const ACTION_TYPE_SPONSORED_AD: MarketingActionVariantEnum[] = [
    MarketingActionVariantEnum.Classic,
    MarketingActionVariantEnum.AutoRule,
];

export function useActionTypeFilter({
    defaultValue = ACTION_TYPE_DEFAULT_VALUE,
    marketingActionType,
    initialValue,
}: UseActionTypeFilterOptions = {}): UseActionTypeFilterResult {
    const { user } = useAuth();
    const isDisplaySmart = displaySmartScheduler(user?.organization?.id?.toString() ?? "");
    const [actionTypes, setActionTypes] = useState<UseActionTypeFilterValue>(
        initialValue ?? defaultValue
    );
    const resetFilter = useCallback(
        () => setActionTypes(defaultValue),
        [setActionTypes, defaultValue]
    );
    const { translateCommon } = useTranslation();

    const currentActionType =
        marketingActionType === MarketingActionType.Offer
            ? ACTION_TYPES_OFFER.filter((e) => {
                  if (isDisplaySmart === false && e === MarketingActionVariantEnum.SmartScheduler) {
                      return false;
                  }
                  return true;
              })
            : ACTION_TYPE_SPONSORED_AD;

    const actionTypeOptions = currentActionType.map((type) => ({
        id: type,
        name: translateCommon(`enums.action-type.${marketingActionType}-${type}`),
    }));

    return {
        actionTypeOptions,
        actionTypes,
        handleActionTypeChange: setActionTypes,
        isEmpty: isActionTypeFilterEmpty(actionTypes),
        resetFilter,
    };
}

export function formatActionTypeFilter(
    actionTypes: UseActionTypeFilterValue = ACTION_TYPE_DEFAULT_VALUE,
    actionTypeOptions: IdStringName[] = []
): string {
    return actionTypes
        .map((acType) => actionTypeOptions.find((actionType) => actionType.id === acType)?.name)
        .join(", ");
}

export function isActionTypeFilterEmpty(
    filter: UseActionTypeFilterValue = ACTION_TYPE_DEFAULT_VALUE
): boolean {
    return filter.length === 0;
}
